import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SortOrder } from '@modules/common/types/sort-order';
import { MailAppStateService } from '@modules/messages/services/mail-app-state.service';
import { StitchTypeFilters } from '@modules/knowledge/types/stitch-type-filters';
import {
  MessageFolderListSortField,
  MessageFoldersListState,
} from '@modules/messages/types/message-folders-list-state';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { merge, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';

const defaultSortOrder: Record<MessageFolderListSortField, SortOrder> = {
  title: SortOrder.ASC,
  createdAt: SortOrder.DESC,
  updatedAt: SortOrder.DESC,
  score: SortOrder.DESC,
};

@Component({
  selector: 'app-folders-list-context-menu',
  templateUrl: './folders-list-context-menu.component.html',
  styleUrls: ['./folders-list-context-menu.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FoldersListContextMenuComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public state: MessageFoldersListState;
  @Input()
  public withAdvancedSearch: boolean;
  @Input()
  public withScore: boolean;

  public advancedSearchApplied: boolean;

  private readonly stateChanged = new Subject<void>();
  private readonly alive = new Subject<void>();

  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly mailAppStateService = inject(MailAppStateService);

  protected readonly virtualFolder = toSignal(this.mailAppStateService.getVirtualFolder());

  public ngOnInit() {
    merge(
      this.stateChanged.pipe(
        startWith(void 0),
        switchMap(() => this.state.changes().pipe(startWith(this.state))),
      ),
      this.advancedSearchService.getApplied().pipe(
        tap((state) => {
          this.advancedSearchApplied = state;
        }),
      ),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.changeDetectorRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('state' in changes) {
      this.stateChanged.next();
    }
  }

  public ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  public sort(sortBy: MessageFolderListSortField) {
    let order = this.state.sortOrder;
    let by = this.state.sortBy;

    if (by === sortBy) {
      order = order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
    } else {
      by = sortBy;
      order = defaultSortOrder[by] ?? SortOrder.ASC;
    }

    this.state.sortBy = by;
    this.state.sortOrder = order;
    this.state.notifyStateChanged();
  }

  public pinnedOnTop() {
    this.state.pinnedOnTop = !this.state.pinnedOnTop;
    this.state.notifyStateChanged();
  }

  public flaggedOnTop() {
    this.state.flaggedOnTop = !this.state.flaggedOnTop;
    this.state.notifyStateChanged();
  }

  public flag() {
    this.state.flagged = this.state.flagged ? null : true;
    this.state.notifyStateChanged();
  }

  public pin() {
    this.state.pinned = this.state.pinned ? null : true;
    this.state.notifyStateChanged();
  }

  public today(): void {
    this.state.today = !this.state.today;
    this.state.notifyStateChanged();
  }

  public stitch(stitch: StitchTypeFilters) {
    this.state.stitchedWith = stitch;
    this.state.notifyStateChanged();
  }
}
